import api from "../api/api";
const BASE_URL = import.meta.env.VITE_APP_API_URL;

export const sapDownloadsService = {
  /**
   * Listar arquivos com filtros
   * @param {Object} filtros - Filtros para a busca
   * @param {string} filtros.status - Status do arquivo (PENDENTE, IMPORTADO, ERRO, CANCELADO)
   * @param {string} filtros.tipo_origem - Tipo de origem (TRANSFERENCIA, ENTRADA, SAIDA)
   * @param {string} filtros.data_inicio - Data de início (YYYY-MM-DD)
   * @param {string} filtros.data_fim - Data de fim (YYYY-MM-DD)
   * @param {number} filtros.per_page - Itens por página
   * @returns {Promise<Object>} Resposta com lista de arquivos
   */
  async listarArquivos(filtros = {}) {
    try {
      const params = new URLSearchParams();
      
      if (filtros.status) params.append('status', filtros.status);
      if (filtros.tipo_origem) params.append('tipo_origem', filtros.tipo_origem);
      if (filtros.data_inicio) params.append('data_inicio', filtros.data_inicio);
      if (filtros.data_fim) params.append('data_fim', filtros.data_fim);
      if (filtros.per_page) params.append('per_page', filtros.per_page);
      if (filtros.page) params.append('page', filtros.page);

      const url = `${BASE_URL}/sap-downloads?${params.toString()}`;
      console.log("📥 Listando arquivos SAP:", url);
      
      const response = await api.get(url);

      if (response.status === 200 && response.data) {
        const paginationData = response.data.data || response.data;
        const arquivos = paginationData.data || paginationData.dados || [];
        
        console.log("✅ Arquivos SAP recebidos:", arquivos.length);
        
        return {
          success: true,
          message: response.data.mensagem || "Arquivos listados com sucesso",
          total: paginationData.total || 0,
          per_page: paginationData.per_page || 15,
          current_page: paginationData.current_page || 1,
          last_page: paginationData.last_page || 1,
          dados: arquivos,
        };
      } else {
        throw new Error(response.data?.mensagem || "Erro ao listar arquivos.");
      }
    } catch (error) {
      console.error("❌ Erro ao listar arquivos SAP:", error);
      return {
        success: false,
        message: error.response?.data?.mensagem || "Erro ao listar arquivos.",
        total: 0,
        dados: [],
      };
    }
  },

  /**
   * Obter estatísticas dos arquivos
   * @returns {Promise<Object>} Resposta com estatísticas
   */
  async estatisticas() {
    try {
      const url = `${BASE_URL}/sap-downloads/estatisticas`;
      console.log("📊 Buscando estatísticas SAP:", url);
      
      const response = await api.get(url);

      if (response.status === 200 && response.data) {
        return {
          success: true,
          message: response.data.mensagem || "Estatísticas carregadas",
          dados: response.data.data || {},
        };
      } else {
        throw new Error(response.data?.mensagem || "Erro ao buscar estatísticas.");
      }
    } catch (error) {
      console.error("❌ Erro ao buscar estatísticas SAP:", error);
      return {
        success: false,
        message: error.response?.data?.mensagem || "Erro ao buscar estatísticas.",
        dados: {},
      };
    }
  },

  /**
   * Buscar arquivo por ID
   * @param {number} id - ID do arquivo
   * @returns {Promise<Object>} Resposta com dados do arquivo
   */
  async buscarArquivo(id) {
    try {
      const url = `${BASE_URL}/sap-downloads/${id}`;
      console.log("🔍 Buscando arquivo SAP:", url);
      
      const response = await api.get(url);

      if (response.status === 200 && response.data) {
        return {
          success: true,
          message: response.data.mensagem || "Arquivo encontrado",
          dados: response.data.data,
        };
      } else {
        throw new Error(response.data?.mensagem || "Erro ao buscar arquivo.");
      }
    } catch (error) {
      console.error("❌ Erro ao buscar arquivo SAP:", error);
      return {
        success: false,
        message: error.response?.data?.mensagem || "Erro ao buscar arquivo.",
        dados: null,
      };
    }
  },

  /**
   * Confirmar importação de arquivo no SAP
   * @param {number} id - ID do arquivo
   * @param {Object} dados - Dados da confirmação
   * @param {string} dados.usuario_importacao - Usuário que fez a importação
   * @param {string} dados.observacao - Observação da importação
   * @returns {Promise<Object>} Resposta da confirmação
   */
  async confirmarImportacao(id, dados = {}) {
    try {
      const url = `${BASE_URL}/sap-downloads/${id}/confirmar`;
      console.log("✅ Confirmando importação SAP:", url, dados);
      
      const response = await api.post(url, dados);

      if (response.status === 200 && response.data) {
        return {
          success: true,
          message: response.data.mensagem || "Importação confirmada com sucesso",
          dados: response.data.data,
        };
      } else {
        throw new Error(response.data?.mensagem || "Erro ao confirmar importação.");
      }
    } catch (error) {
      console.error("❌ Erro ao confirmar importação SAP:", error);
      return {
        success: false,
        message: error.response?.data?.mensagem || "Erro ao confirmar importação.",
      };
    }
  },

  /**
   * Cancelar arquivo Excel
   * @param {number} id - ID do arquivo
   * @param {Object} dados - Dados do cancelamento
   * @param {string} dados.motivo - Motivo do cancelamento
   * @returns {Promise<Object>} Resposta do cancelamento
   */
  async cancelarArquivo(id, dados = {}) {
    try {
      const url = `${BASE_URL}/sap-downloads/${id}/cancelar`;
      console.log("❌ Cancelando arquivo SAP:", url, dados);
      
      const response = await api.post(url, dados);

      if (response.status === 200 && response.data) {
        return {
          success: true,
          message: response.data.mensagem || "Arquivo cancelado com sucesso",
          dados: response.data.data,
        };
      } else {
        throw new Error(response.data?.mensagem || "Erro ao cancelar arquivo.");
      }
    } catch (error) {
      console.error("❌ Erro ao cancelar arquivo SAP:", error);
      return {
        success: false,
        message: error.response?.data?.mensagem || "Erro ao cancelar arquivo.",
      };
    }
  },

  /**
   * Marcar arquivo com erro
   * @param {number} id - ID do arquivo
   * @param {Object} dados - Dados do erro
   * @param {string} dados.mensagem_erro - Mensagem de erro
   * @returns {Promise<Object>} Resposta
   */
  async marcarErro(id, dados) {
    try {
      const url = `${BASE_URL}/sap-downloads/${id}/erro`;
      console.log("⚠️ Marcando erro em arquivo SAP:", url, dados);
      
      const response = await api.post(url, dados);

      if (response.status === 200 && response.data) {
        return {
          success: true,
          message: response.data.mensagem || "Arquivo marcado com erro",
          dados: response.data.data,
        };
      } else {
        throw new Error(response.data?.mensagem || "Erro ao marcar arquivo com erro.");
      }
    } catch (error) {
      console.error("❌ Erro ao marcar erro em arquivo SAP:", error);
      return {
        success: false,
        message: error.response?.data?.mensagem || "Erro ao marcar arquivo com erro.",
      };
    }
  },

  /**
   * Baixar arquivo Excel
   * @param {number} id - ID do arquivo
   * @param {string} nomeArquivo - Nome do arquivo para download
   * @returns {Promise<Object>} Resposta do download
   */
  async baixarArquivo(id, nomeArquivo = 'arquivo.xlsx') {
    try {
      const url = `${BASE_URL}/sap-downloads/${id}/download`;
      console.log("📥 Baixando arquivo SAP:", url);
      
      const response = await api.get(url, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      });

      if (response.status === 200) {
        // Extrair nome do arquivo do header Content-Disposition
        const contentDisposition = response.headers['content-disposition'];
        let fileName = nomeArquivo;
        
        if (contentDisposition) {
          const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
          if (matches != null && matches[1]) {
            fileName = matches[1].replace(/['"]/g, '');
          }
        }

        // Criar URL para download
        const blob = new Blob([response.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        const downloadUrl = window.URL.createObjectURL(blob);
        
        // Criar link temporário e fazer download
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Limpar URL temporária
        window.URL.revokeObjectURL(downloadUrl);

        return {
          success: true,
          message: "Arquivo baixado com sucesso",
          nomeArquivo: fileName,
        };
      } else {
        throw new Error("Erro ao baixar arquivo.");
      }
    } catch (error) {
      console.error("❌ Erro ao baixar arquivo SAP:", error);
      return {
        success: false,
        message: error.response?.data?.mensagem || "Erro ao baixar arquivo.",
      };
    }
  },
};

export default sapDownloadsService;
